import { useState, useEffect, useMemo, useRef } from 'react'
import {
  Clock,
  RefreshCw,
  Copy,
  Check,
  ExternalLink,
  Tag,
  Truck,
  Gift,
  Percent,
  ShoppingBag,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  AlertCircle,
  Sparkles,
  Loader2,
  CheckCircle,
  Timer
} from 'lucide-react'

const ITEMS_PER_PAGE = 12
import { getFunctions, httpsCallable } from 'firebase/functions'
import { collection, query, orderBy, getDocs } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { Vendor, VendorPromotion } from '../../types/vendor'
import { getVendors } from '../../services/vendorService'
import { getEmailById, Email, markEmails } from '../../services/emailService'

interface PromotionWithVendor extends VendorPromotion {
  vendor: Vendor
}

interface VendorPromoGroup {
  vendor: Vendor
  promotions: PromotionWithVendor[]
}

interface PromotionsTabProps {
  userId: string
  onRefresh?: () => void
}

type OfferCategory = 'savings' | 'code' | 'shipping' | 'bogo' | 'trial' | 'expiring' | 'default'

/**
 * Determines the category and color scheme for an offer
 */
function getOfferCategory(promo: VendorPromotion): OfferCategory {
  // Check if expiring soon (within 3 days) - this takes priority
  if (promo.expiresAt) {
    const expiresDate = promo.expiresAt instanceof Date ? promo.expiresAt :
      (typeof (promo.expiresAt as { toDate?: () => Date })?.toDate === 'function'
        ? (promo.expiresAt as { toDate: () => Date }).toDate()
        : new Date(promo.expiresAt as unknown as string))
    const daysLeft = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    if (daysLeft >= 0 && daysLeft <= 3) {
      return 'expiring'
    }
  }

  // Check discount type
  if (promo.discountType) {
    switch (promo.discountType) {
      case 'free_shipping':
        return 'shipping'
      case 'bogo':
        return 'bogo'
      case 'free_trial':
        return 'trial'
    }
  }

  // Check for savings (% off, $ off)
  if (promo.discountValue && promo.discountValue.trim()) {
    return 'savings'
  }

  // Check for promo code
  if (promo.promoCode && promo.promoCode.trim()) {
    return 'code'
  }

  return 'default'
}

/**
 * Get color classes based on offer category
 */
function getCategoryColors(category: OfferCategory): { gradient: string; badge: string; text: string } {
  switch (category) {
    case 'savings':
      return {
        gradient: 'from-emerald-500 to-green-600',
        badge: 'bg-emerald-100 text-emerald-700',
        text: 'text-emerald-600'
      }
    case 'code':
      return {
        gradient: 'from-violet-500 to-purple-600',
        badge: 'bg-violet-100 text-violet-700',
        text: 'text-violet-600'
      }
    case 'shipping':
      return {
        gradient: 'from-blue-500 to-cyan-600',
        badge: 'bg-blue-100 text-blue-700',
        text: 'text-blue-600'
      }
    case 'bogo':
      return {
        gradient: 'from-pink-500 to-rose-600',
        badge: 'bg-pink-100 text-pink-700',
        text: 'text-pink-600'
      }
    case 'trial':
      return {
        gradient: 'from-teal-500 to-cyan-600',
        badge: 'bg-teal-100 text-teal-700',
        text: 'text-teal-600'
      }
    case 'expiring':
      return {
        gradient: 'from-orange-500 to-amber-600',
        badge: 'bg-orange-100 text-orange-700',
        text: 'text-orange-600'
      }
    default:
      return {
        gradient: 'from-indigo-500 to-purple-600',
        badge: 'bg-indigo-100 text-indigo-700',
        text: 'text-indigo-600'
      }
  }
}

/**
 * Get icon for offer category
 */
function getCategoryIcon(category: OfferCategory) {
  switch (category) {
    case 'savings':
      return Percent
    case 'code':
      return Tag
    case 'shipping':
      return Truck
    case 'bogo':
      return Gift
    case 'trial':
      return Sparkles
    case 'expiring':
      return AlertCircle
    default:
      return Tag
  }
}

/**
 * Gets the hero text for a promotion
 */
function getOfferHero(promo: VendorPromotion): string {
  if (promo.discountValue && promo.discountValue.trim()) {
    return promo.discountValue
  }
  if (promo.promoCode && promo.promoCode.trim()) {
    return `CODE: ${promo.promoCode}`
  }
  if (promo.discountType) {
    switch (promo.discountType) {
      case 'free_shipping':
        return 'FREE SHIPPING'
      case 'bogo':
        return 'BUY ONE GET ONE'
      case 'free_trial':
        return 'FREE TRIAL'
      case 'percentage':
        return 'DISCOUNT'
      case 'fixed':
        return 'SAVINGS'
    }
  }

  // Use title/description
  const offerText = promo.title || promo.description || ''
  if (offerText) {
    const cleaned = offerText.replace(/^(re:|fwd:|fw:)\s*/i, '').trim()
    let shortText = cleaned.substring(0, 25)
    const breakPoints = [' - ', ': ', '! ', '. ', ' | ']
    for (const bp of breakPoints) {
      const idx = cleaned.indexOf(bp)
      if (idx > 5 && idx < 30) {
        shortText = cleaned.substring(0, idx)
        break
      }
    }
    if (shortText.length > 20) {
      const lastSpace = shortText.lastIndexOf(' ')
      if (lastSpace > 10) {
        shortText = shortText.substring(0, lastSpace)
      }
    }
    return shortText.toUpperCase()
  }

  return 'OFFER'
}

export default function PromotionsTab({ userId, onRefresh }: PromotionsTabProps) {
  const [promotions, setPromotions] = useState<PromotionWithVendor[]>([])
  const [loading, setLoading] = useState(true)
  const [isScanning, setIsScanning] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [clearingVendors, setClearingVendors] = useState<Set<string>>(new Set())
  const [exitingVendors, setExitingVendors] = useState<Set<string>>(new Set())
  const [scanResult, setScanResult] = useState<string | null>(null)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null)
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null)
  const [, setLoadingEmail] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  useEffect(() => {
    loadPromotions()
  }, [userId])

  const loadPromotions = async () => {
    setLoading(true)
    try {
      const vendors = await getVendors(userId)
      const vendorMap = new Map(vendors.map(v => [v.id, v]))

      const promotionsRef = collection(db, 'users', userId, 'promotions')
      const q = query(promotionsRef, orderBy('receivedAt', 'desc'))
      const snapshot = await getDocs(q)

      const promoList: PromotionWithVendor[] = []

      for (const doc of snapshot.docs) {
        const data = doc.data()
        const vendor = vendorMap.get(data.vendorId)
        if (vendor) {
          const promo: PromotionWithVendor = {
            id: doc.id,
            vendorId: data.vendorId,
            emailId: data.emailId,
            title: data.title || '',
            description: data.description || '',
            discountType: data.discountType,
            discountValue: data.discountValue,
            promoCode: data.promoCode,
            expiresAt: data.expiresAt?.toDate?.() || data.expiresAt,
            productCategory: data.productCategory,
            isExpired: data.isExpired || false,
            isUsed: data.isUsed || false,
            isDismissed: data.isDismissed || false,
            receivedAt: data.receivedAt?.toDate?.() || new Date(),
            createdAt: data.createdAt?.toDate?.() || new Date(),
            vendor
          }
          promoList.push(promo)
        }
      }

      setPromotions(promoList)
    } catch (error) {
      console.error('Failed to load promotions:', error)
    } finally {
      setLoading(false)
    }
  }

  // Group promotions by vendor (exclude vendors marked as human)
  const allVendorGroups = useMemo(() => {
    const filtered = promotions.filter(p => !p.isDismissed && !p.isExpired && !p.vendor.isHuman)

    // Group by vendor
    const groups = new Map<string, VendorPromoGroup>()
    for (const promo of filtered) {
      const vendorId = promo.vendorId
      if (!groups.has(vendorId)) {
        groups.set(vendorId, { vendor: promo.vendor, promotions: [] })
      }
      groups.get(vendorId)!.promotions.push(promo)
    }

    return Array.from(groups.values()).sort((a, b) => {
      const aDate = a.promotions[0]?.receivedAt instanceof Date ? a.promotions[0].receivedAt : new Date()
      const bDate = b.promotions[0]?.receivedAt instanceof Date ? b.promotions[0].receivedAt : new Date()
      return bDate.getTime() - aDate.getTime()
    })
  }, [promotions])

  // Pagination
  const totalPages = Math.ceil(allVendorGroups.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const vendorGroups = allVendorGroups.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  // Total promotion count (not vendor groups)
  const totalPromotionCount = promotions.filter(p => !p.isDismissed && !p.isExpired).length

  const handleMarkAllCleared = async () => {
    const activePromotions = promotions.filter(p => !p.isDismissed && !p.isExpired)
    if (activePromotions.length === 0) return

    if (!confirm(`Clear all ${activePromotions.length} promotions?`)) return

    // Get all vendor IDs to animate
    const vendorIds = [...new Set(activePromotions.map(p => p.vendorId))]

    // Start clearing animation for all
    setClearingVendors(new Set(vendorIds))

    try {
      // Get all email IDs from active promotions (remove angle brackets for Firestore doc IDs)
      const emailIds = [...new Set(activePromotions.map(p => p.emailId.replace(/[<>]/g, '')))]

      const result = await markEmails({
        emailIds,
        action: 'cleared',
        deleteOnExpiry: false
      })

      if (result.success) {
        // Start exit animation for all
        setClearingVendors(new Set())
        setExitingVendors(new Set(vendorIds))

        // After exit animation, remove from state
        setTimeout(() => {
          setPromotions(prev => prev.map(p => ({ ...p, isDismissed: true })))
          setExitingVendors(new Set())
        }, 300)

        onRefresh?.()
      } else {
        setClearingVendors(new Set())
        setActionResult({ type: 'error', message: 'Failed to clear emails' })
        setTimeout(() => setActionResult(null), 3000)
      }
    } catch (error) {
      console.error('Failed to clear emails:', error)
      setClearingVendors(new Set())
      setActionResult({ type: 'error', message: 'Failed to clear emails' })
      setTimeout(() => setActionResult(null), 3000)
    }
  }

  const handleMarkVendorCleared = async (vendorId: string) => {
    // Get all email IDs for this vendor from the promotions we have loaded
    const vendorPromotions = promotions.filter(p => p.vendorId === vendorId && !p.isDismissed && !p.isExpired)

    // Convert messageIds to Firestore doc IDs by removing angle brackets
    const emailIds = [...new Set(vendorPromotions.map(p => p.emailId.replace(/[<>]/g, '')))]

    if (emailIds.length === 0) {
      setActionResult({ type: 'error', message: 'No emails found for this vendor' })
      setTimeout(() => setActionResult(null), 3000)
      return
    }

    // Start clearing animation
    setClearingVendors(prev => new Set(prev).add(vendorId))

    try {
      const result = await markEmails({
        emailIds,
        action: 'cleared',
        deleteOnExpiry: false
      })

      if (result.success) {
        // Start exit animation
        setClearingVendors(prev => {
          const next = new Set(prev)
          next.delete(vendorId)
          return next
        })
        setExitingVendors(prev => new Set(prev).add(vendorId))

        // After exit animation, remove from state
        setTimeout(() => {
          setPromotions(prev => prev.map(p =>
            p.vendorId === vendorId ? { ...p, isDismissed: true } : p
          ))
          setExitingVendors(prev => {
            const next = new Set(prev)
            next.delete(vendorId)
            return next
          })
        }, 300) // Match animation duration

        onRefresh?.()
      } else {
        setClearingVendors(prev => {
          const next = new Set(prev)
          next.delete(vendorId)
          return next
        })
        setActionResult({ type: 'error', message: 'Failed to clear emails' })
        setTimeout(() => setActionResult(null), 3000)
      }
    } catch (error) {
      console.error('Failed to clear vendor emails:', error)
      setClearingVendors(prev => {
        const next = new Set(prev)
        next.delete(vendorId)
        return next
      })
      setActionResult({ type: 'error', message: 'Failed to clear emails' })
      setTimeout(() => setActionResult(null), 3000)
    }
  }

  const handleResetAndRescan = async () => {
    if (!confirm('This will delete all vendors and promotions data and rescan all emails. Continue?')) {
      return
    }

    setIsResetting(true)
    setScanResult('Resetting data...')
    try {
      const functions = getFunctions()
      const resetAIData = httpsCallable(functions, 'resetAIData')
      await resetAIData({})
      setScanResult('Data reset. Now scanning emails...')

      setIsResetting(false)
      setIsScanning(true)
      const reprocessEmailsForDeals = httpsCallable(functions, 'reprocessEmailsForDeals')
      const result = await reprocessEmailsForDeals({ limit: 100 })
      const data = result.data as { message: string }
      setScanResult(data.message)
      await loadPromotions()
      onRefresh?.()
    } catch (error) {
      console.error('Failed to reset and rescan:', error)
      setScanResult('Failed to reset. Please try again.')
    } finally {
      setIsResetting(false)
      setIsScanning(false)
    }
  }

  const handleScanEmails = async () => {
    setIsScanning(true)
    setScanResult(null)
    try {
      const functions = getFunctions()
      const reprocessEmailsForDeals = httpsCallable(functions, 'reprocessEmailsForDeals')
      const result = await reprocessEmailsForDeals({ limit: 100 })
      const data = result.data as { message: string }
      setScanResult(data.message)
      await loadPromotions()
      onRefresh?.()
    } catch (error) {
      console.error('Failed to scan emails:', error)
      setScanResult('Failed to scan emails. Please try again.')
    } finally {
      setIsScanning(false)
    }
  }

  const handleCopyCode = async (code: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(null), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const handleViewEmail = async (promo: PromotionWithVendor) => {
    setLoadingEmail(true)
    try {
      const email = await getEmailById(userId, promo.emailId)
      if (email) {
        setSelectedEmail(email)
      }
    } catch (error) {
      console.error('Failed to load email:', error)
    } finally {
      setLoadingEmail(false)
    }
  }

  const handleTileClick = (group: VendorPromoGroup) => {
    if (group.promotions.length === 1) {
      handleViewEmail(group.promotions[0])
    } else {
      setExpandedVendor(expandedVendor === group.vendor.id ? null : group.vendor.id!)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Tag className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
              Promotions
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {totalPromotionCount} {totalPromotionCount === 1 ? 'promotion' : 'promotions'} from {allVendorGroups.length} {allVendorGroups.length === 1 ? 'vendor' : 'vendors'}
              {totalPages > 1 && (
                <span className="text-gray-400"> • Page {currentPage} of {totalPages}</span>
              )}
            </p>
          </div>
          {/* Mobile: horizontal scroll for buttons, Desktop: flex wrap */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 sm:overflow-visible -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap sm:justify-end">
            {allVendorGroups.length > 0 && (
              <button
                onClick={handleMarkAllCleared}
                disabled={clearingVendors.size > 0 || isScanning || isResetting}
                className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-2 min-h-[44px] text-xs sm:text-sm font-medium rounded-lg border border-green-300 text-green-700 hover:bg-green-50 active:bg-green-100 disabled:opacity-50 transition-colors"
              >
                {clearingVendors.size > 0 ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                <span className="whitespace-nowrap">Clear All</span>
              </button>
            )}
            <button
              onClick={handleResetAndRescan}
              disabled={isScanning || isResetting || clearingVendors.size > 0}
              className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-2 min-h-[44px] text-xs sm:text-sm font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-50 active:bg-red-100 disabled:opacity-50 transition-colors"
            >
              <span className="whitespace-nowrap">{isResetting ? 'Resetting...' : 'Reset & Rescan'}</span>
            </button>
            <button
              onClick={handleScanEmails}
              disabled={isScanning || isResetting || clearingVendors.size > 0}
              className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-2 min-h-[44px] text-xs sm:text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isScanning ? 'animate-spin' : ''}`} />
              <span className="whitespace-nowrap">{isScanning ? 'Scanning...' : 'Scan for Offers'}</span>
            </button>
          </div>
        </div>

        {(scanResult || actionResult) && (
          <div className={`mt-3 p-2.5 sm:p-3 rounded-lg text-xs sm:text-sm ${
            (scanResult?.includes('Failed') || actionResult?.type === 'error')
              ? 'bg-red-50 text-red-700'
              : 'bg-green-50 text-green-700'
          }`}>
            {actionResult?.message || scanResult}
          </div>
        )}
      </div>

      {/* Promotions Grid */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4">
        {allVendorGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 px-4 text-center">
            <ShoppingBag className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4 opacity-50" />
            <p className="text-base sm:text-lg font-medium">No offers found</p>
            <p className="text-xs sm:text-sm mt-1">Click "Scan for Offers" to find deals in your inbox</p>
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 transition-all duration-300">
              {vendorGroups
                .filter(group => !exitingVendors.has(group.vendor.id!) || clearingVendors.has(group.vendor.id!))
                .map(group => (
                <PromotionTile
                  key={group.vendor.id}
                  group={group}
                  isExpanded={expandedVendor === group.vendor.id}
                  isClearing={clearingVendors.has(group.vendor.id!)}
                  isExiting={exitingVendors.has(group.vendor.id!)}
                  onTileClick={() => handleTileClick(group)}
                  onPromoClick={handleViewEmail}
                  onCopyCode={handleCopyCode}
                  copiedCode={copiedCode}
                  onClear={(vendorId) => handleMarkVendorCleared(vendorId)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {/* Email Viewer Modal - responsive */}
      {selectedEmail && (
        <EmailViewerModal
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}

    </div>
  )
}

/**
 * Vendor logo component with graceful fallback
 */
function VendorLogo({ vendor, size = 6 }: { vendor: Vendor; size?: number }) {
  const [imgError, setImgError] = useState(false)

  const sizeClass = size === 6 ? 'w-6 h-6' : size === 8 ? 'w-8 h-8' : 'w-10 h-10'
  const textSize = size === 6 ? 'text-xs' : size === 8 ? 'text-sm' : 'text-base'

  if (vendor.logoUrl && !imgError) {
    return (
      <img
        src={vendor.logoUrl}
        alt={vendor.name}
        className={`${sizeClass} rounded object-contain bg-gray-100`}
        onError={() => setImgError(true)}
      />
    )
  }

  return (
    <div className={`${sizeClass} rounded bg-gray-200 flex items-center justify-center text-gray-600 font-semibold ${textSize}`}>
      {vendor.name.charAt(0).toUpperCase()}
    </div>
  )
}

interface PromotionTileProps {
  group: VendorPromoGroup
  isExpanded: boolean
  isClearing: boolean
  isExiting: boolean
  onTileClick: () => void
  onPromoClick: (promo: PromotionWithVendor) => void
  onCopyCode: (code: string, e: React.MouseEvent) => void
  copiedCode: string | null
  onClear: (vendorId: string) => void
}

function PromotionTile({
  group,
  isExpanded,
  isClearing,
  isExiting,
  onTileClick,
  onPromoClick,
  onCopyCode,
  copiedCode,
  onClear
}: PromotionTileProps) {
  const { vendor, promotions } = group
  const hasMultiple = promotions.length > 1
  const latestPromo = promotions[0]

  const category = getOfferCategory(latestPromo)
  const colors = getCategoryColors(category)
  const IconComponent = getCategoryIcon(category)
  const heroText = getOfferHero(latestPromo)

  const formatReceivedDate = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (hours < 1) return 'Just now'
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  const getExpiryText = (promo: PromotionWithVendor) => {
    if (!promo.expiresAt) return null
    const expiresDate = promo.expiresAt instanceof Date ? promo.expiresAt :
      (typeof (promo.expiresAt as { toDate?: () => Date })?.toDate === 'function'
        ? (promo.expiresAt as { toDate: () => Date }).toDate()
        : new Date(promo.expiresAt as unknown as string))
    const daysLeft = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))

    if (daysLeft < 0) return null
    if (daysLeft === 0) return 'Expires today!'
    if (daysLeft === 1) return 'Expires tomorrow'
    if (daysLeft <= 7) return `${daysLeft} days left`
    return null
  }

  const expiryText = getExpiryText(latestPromo)

  return (
    <div className={`bg-white rounded-xl sm:rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
      isExiting ? 'opacity-0 scale-95 pointer-events-none' :
      isClearing ? 'animate-pulse ring-2 ring-green-400' :
      isExpanded ? 'border-indigo-300 shadow-lg' :
      'border-gray-200 hover:border-gray-300 hover:shadow-lg active:bg-gray-50'
    }`}>
      {/* Colored Header - The Offer Hero */}
      <div
        className={`bg-gradient-to-br ${colors.gradient} p-3 sm:p-4 cursor-pointer`}
        onClick={onTileClick}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
              <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-white/90 flex-shrink-0" />
              {hasMultiple && (
                <span className="px-1.5 sm:px-2 py-0.5 bg-white/20 text-white text-[10px] sm:text-xs rounded-full font-medium whitespace-nowrap">
                  +{promotions.length - 1} more
                </span>
              )}
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white leading-tight break-words">
              {heroText}
            </h3>
            {expiryText && category === 'expiring' && (
              <p className="text-white/90 text-xs sm:text-sm mt-1 flex items-center gap-1">
                <AlertCircle size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span className="truncate">{expiryText}</span>
              </p>
            )}
          </div>
          {hasMultiple && (
            <ChevronDown className={`w-5 h-5 text-white/70 transition-transform flex-shrink-0 ml-2 ${isExpanded ? 'rotate-180' : ''}`} />
          )}
        </div>

        {/* Promo Code on colored section - touch friendly */}
        {latestPromo.promoCode && !heroText.includes(latestPromo.promoCode) && (
          <button
            onClick={(e) => onCopyCode(latestPromo.promoCode!, e)}
            className={`mt-2.5 sm:mt-3 inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 min-h-[44px] sm:min-h-0 rounded-lg font-mono text-xs sm:text-sm transition-colors ${
              copiedCode === latestPromo.promoCode
                ? 'bg-white text-green-700'
                : 'bg-white/20 hover:bg-white/30 active:bg-white/40 text-white border border-white/30'
            }`}
          >
            {copiedCode === latestPromo.promoCode ? (
              <><Check size={14} /> Copied!</>
            ) : (
              <><Copy size={14} /> {latestPromo.promoCode}</>
            )}
          </button>
        )}
      </div>

      {/* Vendor Info Section */}
      <div className="p-3 sm:p-4" onClick={onTileClick}>
        <div className="flex items-center gap-2 mb-2 cursor-pointer">
          <VendorLogo vendor={vendor} size={6} />
          <span className="font-medium text-gray-900 text-sm sm:text-base truncate">{vendor.name}</span>
          {/* Auto-delete indicator */}
          {vendor.autoDeleteAfterDays && vendor.autoDeleteAfterDays > 0 && (
            <span className="flex-shrink-0 flex items-center gap-1 px-1.5 sm:px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] sm:text-xs rounded-full" title={`Auto-deletes after ${vendor.autoDeleteAfterDays} days`}>
              <Timer size={10} className="sm:w-3 sm:h-3" />
              {vendor.autoDeleteAfterDays}d
            </span>
          )}
          <div className="ml-auto flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation()
                vendor.id && onClear(vendor.id)
              }}
              disabled={isClearing}
              className={`p-2 sm:p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center rounded-lg transition-colors ${
                isClearing
                  ? 'text-green-600 bg-green-50'
                  : 'text-gray-400 hover:text-green-600 hover:bg-green-50 active:bg-green-100'
              }`}
              title="Clear"
            >
              {isClearing ? (
                <Loader2 size={18} className="sm:w-4 sm:h-4 animate-spin" />
              ) : (
                <CheckCircle size={18} className="sm:w-4 sm:h-4" />
              )}
            </button>
            {vendor.domain && (
              <a
                href={`https://${vendor.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 sm:p-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center text-gray-400 hover:text-indigo-600 active:text-indigo-700 transition-colors"
              >
                <ExternalLink size={16} className="sm:w-3.5 sm:h-3.5" />
              </a>
            )}
          </div>
        </div>

        <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 mb-2 sm:mb-3">
          {latestPromo.description || latestPromo.title}
        </p>

        <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Clock size={10} className="sm:w-3 sm:h-3" />
            {formatReceivedDate(latestPromo.receivedAt instanceof Date ? latestPromo.receivedAt : new Date())}
          </span>
          {expiryText && category !== 'expiring' && (
            <span className="text-orange-600 font-medium">{expiryText}</span>
          )}
        </div>
      </div>

      {/* Expanded List of Additional Promos */}
      {hasMultiple && isExpanded && (
        <div className="border-t border-gray-100">
          {promotions.map((promo, index) => (
            <div
              key={promo.id}
              className={`p-3 sm:p-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors min-h-[60px] flex items-center ${
                index !== promotions.length - 1 ? 'border-b border-gray-100' : ''
              }`}
              onClick={() => onPromoClick(promo)}
            >
              <div className="flex items-center gap-2.5 sm:gap-3 w-full">
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br ${getCategoryColors(getOfferCategory(promo)).gradient} flex items-center justify-center flex-shrink-0`}>
                  {(() => {
                    const Icon = getCategoryIcon(getOfferCategory(promo))
                    return <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                    {getOfferHero(promo)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                    {promo.description || promo.title}
                  </p>
                </div>
                {promo.promoCode && (
                  <button
                    onClick={(e) => onCopyCode(promo.promoCode!, e)}
                    className={`p-2 sm:px-2 sm:py-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center rounded font-mono text-xs transition-colors flex-shrink-0 ${
                      copiedCode === promo.promoCode
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 active:bg-indigo-100'
                    }`}
                  >
                    {copiedCode === promo.promoCode ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface PaginationControlsProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function PaginationControls({ currentPage, totalPages, onPageChange }: PaginationControlsProps) {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const maxVisible = isMobile ? 3 : 5

  const getPageNumbers = () => {
    const pages: number[] = []
    const numPages = Math.min(maxVisible, totalPages)

    for (let i = 0; i < numPages; i++) {
      let pageNum: number
      if (totalPages <= maxVisible) {
        pageNum = i + 1
      } else if (currentPage <= Math.ceil(maxVisible / 2)) {
        pageNum = i + 1
      } else if (currentPage >= totalPages - Math.floor(maxVisible / 2)) {
        pageNum = totalPages - maxVisible + 1 + i
      } else {
        pageNum = currentPage - Math.floor(maxVisible / 2) + i
      }
      pages.push(pageNum)
    }
    return pages
  }

  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
      <button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="p-2 sm:p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronLeft size={20} />
      </button>

      {getPageNumbers().map((pageNum) => (
        <button
          key={pageNum}
          onClick={() => onPageChange(pageNum)}
          className={`min-w-[40px] sm:min-w-[44px] min-h-[44px] px-2 sm:px-3 text-sm font-medium rounded-lg transition-colors ${
            currentPage === pageNum
              ? 'bg-indigo-600 text-white'
              : 'hover:bg-gray-100 active:bg-gray-200 text-gray-700'
          }`}
        >
          {pageNum}
        </button>
      ))}

      <button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="p-2 sm:p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronRight size={20} />
      </button>
    </div>
  )
}

interface EmailViewerModalProps {
  email: Email
  onClose: () => void
}

function EmailViewerModal({ email, onClose }: EmailViewerModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    if (iframeRef.current && email.htmlBody) {
      const doc = iframeRef.current.contentDocument
      if (doc) {
        doc.open()
        doc.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <base target="_blank">
              <style>
                body {
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                  font-size: 14px;
                  line-height: 1.6;
                  color: #374151;
                  padding: 24px;
                  margin: 0;
                  background: white;
                }
                img { max-width: 100%; height: auto; }
                a { color: #4f46e5; }
                table { max-width: 100%; }
              </style>
            </head>
            <body>${email.htmlBody}</body>
          </html>
        `)
        doc.close()
      }
    }
  }, [email.htmlBody])

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-4xl h-[90vh] sm:h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - touch friendly close button */}
        <div className="flex items-start justify-between p-3 sm:p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0 pr-2 sm:pr-4">
            <h3 className="font-semibold text-gray-900 text-base sm:text-lg line-clamp-2">{email.subject}</h3>
            <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 mt-1.5 sm:mt-2 text-xs sm:text-sm text-gray-600">
              <span className="font-medium truncate">{email.fromName || email.from}</span>
              <span className="text-gray-400 hidden sm:inline">•</span>
              <span className="text-gray-500">{formatDate(email.date instanceof Date ? email.date : new Date())}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 sm:p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 active:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
          >
            <X size={22} className="sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {email.htmlBody ? (
            <iframe
              ref={iframeRef}
              title="Email content"
              className="w-full h-full border-0"
              sandbox="allow-same-origin"
            />
          ) : (
            <div className="h-full overflow-y-auto p-4 sm:p-6">
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-700 text-sm sm:text-base">
                {email.body}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

